import { isNil } from 'lodash';

import {
  BackendVsSlaStatus,
  TRAILING_WINDOW_LABEL,
  TurnTimeSlaUnit,
  TurnTimeSlaUnitSuffix,
  VsSlaStatus,
} from './turnTimeMetrics.constants';

const FALLBACK_UNIT_SUFFIX = '';

// Backend statuses that mean "no usable aggregate" — the metric is dropped so
// the board/pillar renders its empty state instead of a misleading zero.
const DROPPED_BACKEND_STATUSES = [BackendVsSlaStatus.NO_SLA, BackendVsSlaStatus.INSUFFICIENT_DATA];

const getUnitSuffix = unit => TurnTimeSlaUnitSuffix[unit] ?? FALLBACK_UNIT_SUFFIX;

/**
 * Resolves the three-bucket homepage vs-SLA display status from the backend
 * status plus the real median/target. WITHIN splits into AHEAD (median strictly
 * under target) or ON_TARGET (median at target); AT_RISK / BREACHED -> BEHIND.
 * @param {object} params
 * @param {string} params.backendStatus - TurnTimeMetricModel.vsSlaStatus
 * @param {number} params.medianValue
 * @param {number} params.slaTarget
 * @returns {string} one of VsSlaStatus
 */
export const resolveVsSlaStatus = ({ backendStatus, medianValue, slaTarget }) => {
  if (backendStatus === BackendVsSlaStatus.WITHIN) {
    return medianValue < slaTarget ? VsSlaStatus.AHEAD : VsSlaStatus.ON_TARGET;
  }

  return VsSlaStatus.BEHIND;
};

/**
 * Maps a single backend turn-time metric (TurnTimeMetricModel) into the homepage
 * display shape. Returns null when the backend reports no usable aggregate
 * (NO_SLA / INSUFFICIENT_DATA) or the median is missing.
 * @param {object} metric - backend metric
 * @returns {object|null} display metric, or null when not renderable
 */
export const mapTurnTimeMetric = metric => {
  const { definitionId, name, medianValue, unit, sampleN, slaTarget, vsSlaStatus } = metric;

  if (isNil(medianValue) || DROPPED_BACKEND_STATUSES.includes(vsSlaStatus)) {
    return null;
  }

  const resolvedUnit = unit ?? TurnTimeSlaUnit.DAYS;

  return {
    definitionId,
    name,
    medianValue,
    unit: resolvedUnit,
    unitSuffix: getUnitSuffix(resolvedUnit),
    sampleN,
    trailingWindowLabel: TRAILING_WINDOW_LABEL,
    slaTarget,
    vsSlaStatus: resolveVsSlaStatus({ backendStatus: vsSlaStatus, medianValue, slaTarget }),
  };
};

/**
 * Maps the compute endpoint's metric array into homepage display metrics,
 * dropping any the backend cannot render (no SLA / insufficient data).
 * @param {object[]} metrics - backend metrics
 * @returns {object[]} display metrics
 */
export const mapTurnTimeMetrics = (metrics = []) =>
  metrics.map(mapTurnTimeMetric).filter(metric => !isNil(metric));
